"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { logger } from "@/src/lib/logging/logger";
import { Appointment, AppointmentMetadataFiltersParams } from "@/src/models/appointment";
import {
  getAppointmentById,
  getPaginatedAppointmentsMetadata,
  updateAppointmentById,
} from "@/src/requests/appointment";
import { isNotFound } from "@/src/requests/utils/fetch-json";

export const PAGINATED_APPOINTMENTS_CACHE_KEY = "paginated-appointments-metadata";

export const APPOINTMENTS_CACHE_KEY = ["appointments"] as const;

export function updateAppointmentsCache(queryClient: ReturnType<typeof useQueryClient>, incoming: Appointment[]) {
  queryClient.setQueryData(APPOINTMENTS_CACHE_KEY, (old: Appointment[] = []) => {
    const map = new Map(old.map((a) => [a.id, a]));

    incoming.forEach((a) => map.set(a.id, a));

    return Array.from(map.values());
  });
}

export function useAppointmentById(id: string) {
  const queryClient = useQueryClient();

  const {
    data: appointment,
    isFetching,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [...APPOINTMENTS_CACHE_KEY, id],
    queryFn: async () => {
      // First check if the appointment is in the global cache
      const globalAppointments = queryClient.getQueryData<Appointment[]>(APPOINTMENTS_CACHE_KEY);
      const cachedAppointment = globalAppointments?.find((apt) => apt.id === id || apt.displayId === id);

      if (cachedAppointment) {
        return cachedAppointment;
      }

      try {
        const appointment = await getAppointmentById(id);

        if (appointment) {
          queryClient.setQueryData([...APPOINTMENTS_CACHE_KEY, id], appointment);

          updateAppointmentsCache(queryClient, [appointment]);
        }

        return appointment;
      } catch (err) {
        if (isNotFound(err as Error)) {
          return null;
        }

        logger.error("Error fetching appointment", {
          error: err,
        });

        throw err;
      }
    },
    enabled: !!id,
    staleTime: Infinity,
  });

  return { appointment, isFetching, isLoading, isError };
}

export function usePaginatedAppointmentsMetadata(params: AppointmentMetadataFiltersParams) {
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      PAGINATED_APPOINTMENTS_CACHE_KEY,
      params.page,
      params.pageSize,
      params.sortBy,
      params.sortOrder,
      params.createdBy,
    ],
    queryFn: () => getPaginatedAppointmentsMetadata(params),
    staleTime: 30_000,
  });

  return {
    appointments: data?.appointments ?? [],
    totalCount: data?.totalCount,
    isLoading,
    isError,
  };
}

export function useUpdateAppointmentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Pick<Partial<Appointment>, "ehrId" | "createdBy"> }) => {
      return updateAppointmentById(id, updates);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData([...APPOINTMENTS_CACHE_KEY, updated.id], updated);
      queryClient.setQueryData([...APPOINTMENTS_CACHE_KEY, updated.displayId], updated);
      updateAppointmentsCache(queryClient, [updated]);
      void queryClient.invalidateQueries({ queryKey: [PAGINATED_APPOINTMENTS_CACHE_KEY] });
    },
    onError: (error) => {
      logger.error("Error updating appointment", { error });
    },
  });
}
